// src/components/profile/ProfileHeader.tsx
// Komponen header profil dengan foto dan informasi bio

import React from 'react';
import { Youtube, Instagram } from 'lucide-react';
import BlurText from '../../TextAnimations/BlurText/BlurText';
import { useOptimizedProfile } from '../../hooks/useOptimizedProfile';
import { useSocialLinks } from '../../hooks/useSocialLinks';
import { PhotoView } from 'react-photo-view';
import LikeButton from '../common/LikeButton';

const TikTokIcon: React.FC<{ className?: string }> = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.47V10.89a8.27 8.27 0 0 0 5.77 2.31v-3.45a4.85 4.85 0 0 1-3.77-3.06z" />
  </svg>
);

const ProfileHeader: React.FC = () => {
  const { profileData, loading, error } = useOptimizedProfile();
  const { socialLinks } = useSocialLinks();





  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="animate-pulse">
          <div className="w-32 h-32 bg-gray-300 rounded-full mx-auto mb-4"></div>
          <div className="h-6 bg-gray-300 rounded w-48 mx-auto mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-64 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="text-red-500">
          <p className="text-lg font-semibold mb-2">Error memuat data profil</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <p className="text-gray-500">Data profil tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md sm:shadow-lg overflow-hidden border border-gray-100 sm:border-gray-200">
        {/* Background Pattern */}
        <div className="h-28 sm:h-32 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 relative">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>
          {/* Decorative circles */}
          <div className="absolute top-4 right-4 w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full animate-pulse"></div>
          <div className="absolute top-8 right-12 w-6 h-6 sm:w-8 sm:h-8 bg-white/30 rounded-full animate-pulse"></div>
          <div className="absolute bottom-4 left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full animate-pulse"></div>
        </div>

        <div className="px-4 pb-5 sm:px-6 sm:pb-6">
          {/* Profile Card */}
          <div className="flex justify-center -mt-12 sm:-mt-16 mb-4 sm:mb-6">
            <div className="relative group">
              <PhotoView src={profileData.profileImageUrl}>
                <img
                  src={profileData.profileImageUrl}
                  alt={`Foto profil ${profileData.fullName}`}
                  className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  loading="lazy"
                />
              </PhotoView>
              {/* Online indicator */}
              <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 border-2 border-white rounded-full animate-pulse shadow-sm"></div>
            </div>
          </div>

          {/* Name and Bio */}
          <div className="text-center mb-4 sm:mb-6">
            <BlurText 
              text={profileData.fullName}
              className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1.5 sm:mb-2 tracking-tight"
              animateBy="words"
              direction="top"
              delay={10}
              stepDuration={0.6}
              onAnimationComplete={() => {}}
            />
            <BlurText 
              text={profileData.bio}
              className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto px-1 sm:px-0"
              animateBy="words"
              direction="bottom"
              delay={10}
              stepDuration={0.4}
              onAnimationComplete={() => {}}
            />
          </div>

          {/* Dukung Vano (Like Interaction) */}
          <div className="flex justify-center my-3 sm:my-4">
            <LikeButton />
          </div>

          {/* Social Media Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 my-4 sm:my-6">
            {socialLinks.youtube && (
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube Vano"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium text-xs sm:text-sm shadow-sm hover:shadow-md transition-all active:scale-95 cursor-pointer"
              >
                <Youtube className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>YouTube</span>
              </a>
            )}
            {socialLinks.tiktok && (
              <a
                href={socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok Vano"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full bg-gray-900 hover:bg-black text-white font-medium text-xs sm:text-sm shadow-sm hover:shadow-md transition-all active:scale-95 cursor-pointer"
              >
                <TikTokIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>TikTok</span>
              </a>
            )}
            {socialLinks.instagram && (
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Vano"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 hover:opacity-95 text-white font-medium text-xs sm:text-sm shadow-sm hover:shadow-md transition-all active:scale-95 cursor-pointer"
              >
                <Instagram className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Instagram</span>
              </a>
            )}
          </div>

          {/* Quick Stats */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 sm:border-gray-200">
            <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div className="bg-blue-50/80 rounded-xl p-2.5 sm:p-3 hover:bg-blue-100/80 transition-colors duration-200 border border-blue-100">
                <div className="text-xl sm:text-2xl font-extrabold text-blue-600">
                  {profileData.achievements?.length || 0}
                </div>
                <div className="text-xs sm:text-sm font-medium text-blue-700">Prestasi</div>
              </div>
              <div className="bg-green-50/80 rounded-xl p-2.5 sm:p-3 hover:bg-green-100/80 transition-colors duration-200 border border-green-100">
                <div className="text-xl sm:text-2xl font-extrabold text-green-600">
                  {profileData.educationHistory?.length || 0}
                </div>
                <div className="text-xs sm:text-sm font-medium text-green-700">Pendidikan</div>
              </div>
              <div className="bg-purple-50/80 rounded-xl p-2.5 sm:p-3 hover:bg-purple-100/80 transition-colors duration-200 border border-purple-100">
                <div className="text-xl sm:text-2xl font-extrabold text-purple-600">
                  {profileData.courseHistory?.length || 0}
                </div>
                <div className="text-xs sm:text-sm font-medium text-purple-700">Kursus</div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ProfileHeader;